import { DateTime } from 'luxon';

export function toDatetime(iso: string): string {
  // The backend's DATETIME() coercion is format-strict and expects
  // milliseconds and an offset (e.g. 2026-06-30T23:59:59.000+00:00).
  // Inputs missing either silently match nothing, so normalize both in —
  // an offset-less input is treated as UTC.
  const withMs = /\.\d{3}/.test(iso)
    ? iso
    : iso.replace(/(Z|[+-]\d{2}:\d{2})?$/, (tz) => `.000${tz}`);
  const withTz = /(Z|[+-]\d{2}:\d{2})$/.test(withMs) ? withMs : `${withMs}Z`;
  return `DATETIME(${withTz.replace(/Z$/, '+00:00')})`;
}

export function getNowDatetime(): string {
  return toDatetime(new Date().toISOString());
}

/** `YYYY-MM-DD` → the DATETIME(...) marker the backend converts to a date. */
export function asOfDatetime(asOfDate: string): string {
  return toDatetime(`${asOfDate}T00:00:00Z`);
}

/**
 * `YYYY-MM-DD` → DATETIME(...) of that day's Swedish midnight, for period
 * bounds on timestamp fields — the same day the columns show (see
 * stockholmDate). Emitted as UTC so DATETIME strings stay comparable.
 */
export function stockholmMidnightDatetime(date: string): string {
  const midnight = DateTime.fromISO(date, { zone: 'Europe/Stockholm' })
    .toUTC()
    .toISO();
  if (!midnight) throw new Error(`Invalid date: ${date}`);
  return toDatetime(midnight);
}

/** `YYYY-MM-DD` → the following day, for exclusive upper date bounds. */
export const nextDay = (date: string): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/** `{$in: ids}` for a non-empty selection; undefined (= no filter) otherwise. */
export const inOrUndefined = (ids?: (string | number)[]) =>
  ids?.length ? { $in: ids } : undefined;

/** `{$in: ids}` for an explicit selection, including an empty match-none one. */
export const explicitInOrUndefined = (ids?: (string | number)[]) =>
  ids ? { $in: ids } : undefined;

/**
 * The endpoint serializes timestamps WITHOUT ms/offset
 * (`2025-09-01T00:00:00`) — normalize to canonical UTC ISO so month keys
 * compare against `currentMonthIso` and parse as UTC in date labels
 * (an offset-less string is otherwise parsed as LOCAL time by `Date`).
 */
export function normalizeMonthIso(raw: string): string {
  const withTz = /(Z|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`;
  return new Date(withTz).toISOString();
}
